import { useCallback, useEffect, useState } from "react";

import { useDeviceId } from "@/lib/device/DeviceProvider";
import {
  markPremiumExplicitlyUnlocked,
  readPremiumExplicitlyUnlocked,
  subscribePremiumUnlockChanged,
} from "@/lib/purchases/premiumUnlockStorage";
import { getPremiumStatus, isPurchasesSupported } from "@/lib/purchases/revenuecat";

const DEV_PREMIUM_SESSION_KEY = "screen0_dev_premium";

export function grantDevPremiumSession() {
  if (!import.meta.env.DEV) return;
  try {
    sessionStorage.setItem(DEV_PREMIUM_SESSION_KEY, "1");
  } catch {
    // sessionStorage may be unavailable in some WebViews
  }
}

function readDevPremiumSession(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return sessionStorage.getItem(DEV_PREMIUM_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function usePremiumAccess() {
  const deviceId = useDeviceId();
  const [storePremium, setStorePremium] = useState(false);
  const [explicitlyUnlocked, setExplicitlyUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [devOverride, setDevOverride] = useState(readDevPremiumSession);

  const refresh = useCallback(async () => {
    if (!deviceId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    if (!isPurchasesSupported()) {
      setStorePremium(false);
      setExplicitlyUnlocked(false);
      setLoading(false);
      return;
    }

    const [status, unlocked] = await Promise.all([
      getPremiumStatus(deviceId),
      readPremiumExplicitlyUnlocked(),
    ]);
    setStorePremium(status?.isPremium ?? false);
    setExplicitlyUnlocked(unlocked);
    setLoading(false);
  }, [deviceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => subscribePremiumUnlockChanged(() => {
    void refresh();
  }), [refresh]);

  const grantDevPremium = useCallback(() => {
    grantDevPremiumSession();
    setDevOverride(true);
    setStorePremium(true);
  }, []);

  const acknowledgePremiumUnlock = useCallback(async () => {
    await markPremiumExplicitlyUnlocked();
    setExplicitlyUnlocked(true);
  }, []);

  const isPremium = devOverride || (storePremium && explicitlyUnlocked);

  return { isPremium, loading, refresh, grantDevPremium, acknowledgePremiumUnlock };
}
